/**
 * duvarSatirlari.js — Proje Beyanı sihirbazının "Duvarlar" adımının saf yardımcıları (backlog #676).
 *
 * ESKİSİ: adım, her stand için aynı sabit L şekilli 6 duvarlı bir krokiydi; gerçek çizimle ilgisi
 * yoktu. ŞİMDİ: GET /projects/{id}/drawn-walls standın ÇİZİLMİŞ duvarlarını döner (fdsVector'ın her
 * kayıtta FDS-Backend'e ittiği kenarlar: uzunluk, çizilen yükseklik ya da null, koridora bakıyor mu)
 * ve sihirbaz çizimdeki HER KENAR için bir satır gösterir. Çizilmemiş bir stand için aynı satırlar
 * elle yazılır ("yazili").
 *
 * Bu dosya React'ten bağımsızdır (yalnızca veri): satırların kurulması, backend'e gidecek gövde,
 * doğrulama ve backend ihlallerinin satırlara dağıtılması. Böylece bileşen ince kalır ve kurallar
 * jest ile denenebilir.
 *
 * ⚠️ NUMARA. Backend gelen listeyi SIRAYLA 1..n diye numaralar ve ihlal alanı `walls.N.height` /
 *    `walls.N.aciklik` bu N'dir: gönderilen listedeki sıra. Duvarı OLMAYAN (işareti kaldırılmış) kenarlar
 *    gönderilmez, bu yüzden satırın görünen numarası "dahil edilen satırlar içindeki sırası"dır —
 *    ekranda gördüğünüz numara ile backend mesajındaki numara hep aynıdır. (`numaralar`)
 * ⚠️ AÇIKLIK. Yalnızca çizimin koridora baktığını söylediği kenarlarda sorulur; fuarın asgari açıklık
 *    yüzdesi (minAciklikYuzde) doluysa ZORUNLUDUR (açıklık yoksa 0 yazılır), boşsa isteğe bağlıdır.
 * ⚠️ BİRDEN ÇOK ŞEKİL. Bir stand birden çok şekil olarak çizilmiş olabilir; her şeklin kendi 0. kenarı
 *    vardır. Bu yüzden bir duvar `shapeId` + `edgeIndex` ile adlandırılır, ikisi de gönderilir.
 */

import { kenarAnahtari } from "./standKroki";

/** Sayıyı input için metne çevirir: "5.000" → "5", 3.5 → "3.5"; boş ya da geçersiz → "". */
export function sayiMetni(v) {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 1000) / 1000);
}

const pozitif = (v) => v !== "" && v != null && Number(v) > 0;

let yaziliSayac = 0;

/** Çizimi olmayan stand için boş (ya da kayıtlı bir duvardan kurulmuş) elle yazılan satır. */
export function yeniYaziliSatir(duvar = null) {
  yaziliSayac += 1;
  return {
    key: `t${yaziliSayac}`,
    tur: "yazili",
    shapeId: null, shapeNo: null, edgeIndex: null,
    uzunlukM: null, koridor: false,
    dahil: true,
    genislik: duvar ? sayiMetni(duvar.width) : "",
    yukseklik: duvar ? sayiMetni(duvar.height) : "",
    aciklik: "",
  };
}

/** Çizilmemiş stand: önceden kaydedilmiş duvarlar varsa onlar, yoksa tek boş satır. */
export function yaziliSatirlar(kayitli = []) {
  const liste = Array.isArray(kayitli) ? kayitli : [];
  return liste.length > 0 ? liste.map((d) => yeniYaziliSatir(d)) : [yeniYaziliSatir()];
}

/** Daha önce kaydedilmiş duvarlar arasında bu çizilmiş kenara denk geleni (şekil + kenar; şekil yazılmamışsa kenar). */
function kayitliKarsilik(kayitli, kenar) {
  return kayitli.find((s) => s.edgeIndex != null && s.edgeIndex === kenar.edgeIndex
    && (s.shapeId == null || s.shapeId === kenar.shapeId)) ?? null;
}

/**
 * Çizimdeki her kenar için bir satır. Başlangıç değerleri:
 *   genişlik   → çizilen uzunluk;
 *   yükseklik  → o kenara çizilen yükseklik, çizilmemişse standın beyan ettiği H Max (boşsa boş);
 *   açıklık    → boş.
 * Daha önce bir duvar listesi kaydedilmişse (`kayitli`) o listedeki değerler gelir ve listede olmayan
 * kenarın duvarı yok sayılır (işareti kalkık başlar).
 */
export function cizilenSatirlar(veri, kayitli = []) {
  const eski = Array.isArray(kayitli) ? kayitli : [];
  const eskiVar = eski.some((s) => s.edgeIndex != null);
  return (veri?.walls ?? []).map((k) => {
    const hit = eskiVar ? kayitliKarsilik(eski, k) : null;
    return {
      key: kenarAnahtari(k.shapeId, k.edgeIndex),   // WAS: `s${k.shapeId}-e${k.edgeIndex}` inline; the sketch (standKroki.js) finds a row by the same key
      tur: "cizili",
      shapeId: k.shapeId, shapeNo: k.shapeNo ?? 1, edgeIndex: k.edgeIndex,
      uzunlukM: k.lengthM, koridor: Boolean(k.facesCorridor),
      dahil: eskiVar ? Boolean(hit) : true,
      genislik: hit ? sayiMetni(hit.width) : sayiMetni(k.lengthM),
      yukseklik: hit ? sayiMetni(hit.height) : sayiMetni(k.heightM ?? veri?.declaredHMax),
      aciklik: hit && hit.aciklik != null ? sayiMetni(hit.aciklik) : "",
    };
  });
}

/** Satır anahtarı → görünen numara (gönderilecek listedeki sıra, 1'den); dahil olmayan satırın numarası yok. */
export function numaralar(satirlar) {
  const out = {};
  let n = 0;
  (satirlar ?? []).forEach((s) => { if (s.dahil) { n += 1; out[s.key] = n; } });
  return out;
}

/** Fuarın asgari açıklık kuralı açık mı (null = kapalı: açıklık isteğe bağlı). */
export const aciklikZorunluMu = (veri) => veri?.minAciklikYuzde != null;

/** Açıklığın kenarın ÇİZİLEN uzunluğuna oranı (%); hesaplanamıyorsa null. Backend de çizilen uzunluğa göre yargılar. */
export function aciklikYuzdesi(satir) {
  if (!satir || satir.tur !== "cizili" || satir.aciklik === "" || !(Number(satir.uzunlukM) > 0)) return null;
  const a = Number(satir.aciklik);
  return Number.isFinite(a) ? (a / Number(satir.uzunlukM)) * 100 : null;
}

/** Dahil edilmiş bir satırın sorunları: kaydetmeden önce kullanıcıya söylenir (backend yine de kendi kuralını uygular). */
export function satirSorunlari(satir, veri) {
  const genislikYok = !pozitif(satir.genislik);
  const yukseklikYok = !pozitif(satir.yukseklik);
  let aciklikEksik = false;
  let aciklikAralikDisi = false;
  if (satir.tur === "cizili" && satir.koridor) {
    if (satir.aciklik === "") {
      aciklikEksik = aciklikZorunluMu(veri);
    } else {
      const a = Number(satir.aciklik);
      aciklikAralikDisi = !(a >= 0) || (pozitif(satir.genislik) && a > Number(satir.genislik));
    }
  }
  return { genislikYok, yukseklikYok, aciklikEksik, aciklikAralikDisi };
}

/** Sorunlu (dahil edilmiş) satırların anahtarları. */
export function sorunluSatirlar(satirlar, veri) {
  return (satirlar ?? []).filter((s) => s.dahil && Object.values(satirSorunlari(s, veri)).some(Boolean)).map((s) => s.key);
}

/** Kaydedilebilir mi: en az bir duvar var ve dahil edilen hiçbir satırda sorun yok. */
export function duvarlarTamam(satirlar, veri) {
  const dahil = (satirlar ?? []).filter((s) => s.dahil);
  return dahil.length > 0 && sorunluSatirlar(satirlar, veri).length === 0;
}

/**
 * POST /declaration/walls gövdesindeki `walls` dizisi: yalnızca duvarı OLAN (dahil) satırlar, çizimden
 * gelenler kenarlarını (`edgeIndex` + `shapeId`) ve — doluysa — açıklıklarını taşır. Elle yazılanlar yalnızca
 * genişlik ve yükseklik taşır (çizime bağlı değiller, çizim kuralları onları yargılayamaz).
 */
export function gonderilecekDuvarlar(satirlar) {
  return (satirlar ?? []).filter((s) => s.dahil).map((s) => {
    const duvar = { width: Number(s.genislik), height: Number(s.yukseklik) };
    if (s.tur === "cizili") {
      duvar.edgeIndex = s.edgeIndex;
      duvar.shapeId = s.shapeId;
      if (s.aciklik !== "") duvar.aciklik = Number(s.aciklik);
    }
    return duvar;
  });
}

const IHLAL_ALANI = /^walls\.(\d+)\.(width|height|aciklik)$/;

/**
 * Backend ihlallerini (KuralIhlaliDTO[]) satırlara dağıtır: `walls.N.height` → N. dahil satırın
 * yüksekliği altına; tanınmayan alanlar (ör. `drawing.wall.2`, alanı olmayanlar) `diger`de kalır ve toplu
 * listede gösterilir — hiçbir ihlal sessizce kaybolmaz.
 * @returns {{ satirlar: Object<string, {width: object[], height: object[], aciklik: object[]}>, diger: object[] }}
 */
export function ihlalleriSatirlaraDagit(satirlar, ihlaller) {
  const sayi = numaralar(satirlar);
  const anahtarOf = {};
  Object.entries(sayi).forEach(([key, n]) => { anahtarOf[n] = key; });
  const out = { satirlar: {}, diger: [] };
  (ihlaller ?? []).forEach((v) => {
    const m = IHLAL_ALANI.exec(v?.field ?? "");
    const key = m ? anahtarOf[Number(m[1])] : null;
    if (!key) { out.diger.push(v); return; }
    const kutu = out.satirlar[key] ?? (out.satirlar[key] = { width: [], height: [], aciklik: [] });
    kutu[m[2]].push(v);
  });
  return out;
}
